import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import ProficiencyGridPage from "./ProficiencyGridPage";
import TraditionalGradesGrid from "../components/TraditionalGradesGrid";
import { downloadFromApi } from "../lib/download";
import {
  listSubjectNarratives, upsertSubjectNarrative,
  listStudentComments, upsertStudentComment,
  progressReportPdfUrl, progressReportBatchPdfUrl,
  saveProgressReportsToFiles, getProgressArchiveStatus,
  getProgressEligibleStudents,
  getAllSchoolYears, getSchoolSettings,
} from "../lib/api";
import { semesterOptions, isSummerSemester } from "../lib/semesters";

const DEFAULT_NARRATIVE_SUBJECTS = ["ELA", "Global Citizenship", "Math", "Science"];

function currentSemester() {
  const d = new Date();
  const m = d.getMonth();
  const y = d.getFullYear();
  if (m >= 7) return { semester: `Fall-${y}`, school_year: `${y}-${y + 1}` };
  return { semester: `Spring-${y}`, school_year: `${y - 1}-${y}` };
}

export default function ProgressTrackingPage() {
  const [params] = useSearchParams();
  const [{ semester, school_year }, setPeriod] = useState(currentSemester());
  const [activeTab, setActiveTab] = useState(params.get("tab") || "grid");
  const [years, setYears] = useState([]);
  const [gradingSystem, setGradingSystem] = useState("proficiency");

  // Semester dropdown options (Fall / Spring / Summer) for the selected school year.
  const semOptions = useMemo(() => semesterOptions(school_year), [school_year]);

  // Load the school's grading system (proficiency | traditional).
  useEffect(() => {
    getSchoolSettings()
      .then((res) => setGradingSystem(res.data.grading_system || "proficiency"))
      .catch(() => {});
  }, []);

  // Load configured school years once.
  useEffect(() => {
    getAllSchoolYears()
      .then((res) => setYears(res.data.years || []))
      .catch(() => {});
  }, []);

  // If the selected semester isn't valid for the chosen school year, snap to Fall.
  useEffect(() => {
    const values = semOptions.map((o) => o.value);
    if (values.length && !values.includes(semester)) {
      setPeriod((p) => ({ ...p, semester: values[0] }));
    }
  }, [semOptions, semester]);

  const yearChoices = years.length ? years : [school_year];

  return (
    <div className="space-y-4 p-4" data-testid="progress-tracking-page">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Progress Tracking</CardTitle>
              <CardDescription>
                Manage proficiency scoring, semester narratives, per-student comments, and generate printable progress reports.
              </CardDescription>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <Label className="text-xs">School Year</Label>
                <Select value={school_year} onValueChange={(v) => setPeriod((p) => ({ ...p, school_year: v }))}>
                  <SelectTrigger className="w-36" data-testid="pt-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearChoices.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Semester</Label>
                {semOptions.length ? (
                  <Select value={semester} onValueChange={(v) => setPeriod((p) => ({ ...p, semester: v }))}>
                    <SelectTrigger className="w-44" data-testid="pt-semester-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {semOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input className="w-44" value={semester} onChange={(e) => setPeriod((p) => ({ ...p, semester: e.target.value }))} data-testid="pt-semester-input" />
                )}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="grid" data-testid="pt-tab-grid">{gradingSystem === "traditional" ? "Grades" : "Scores Grid"}</TabsTrigger>
          <TabsTrigger value="narratives" data-testid="pt-tab-narratives">Subject Narratives</TabsTrigger>
          <TabsTrigger value="comments" data-testid="pt-tab-comments">Student Comments</TabsTrigger>
          <TabsTrigger value="reports" data-testid="pt-tab-reports">Generate Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="grid" className="pt-4">
          {gradingSystem === "traditional"
            ? <TraditionalGradesGrid semester={semester} school_year={school_year} />
            : <ProficiencyGridPage />}
        </TabsContent>
        <TabsContent value="narratives" className="pt-4">
          <SubjectNarrativesTab semester={semester} school_year={school_year} />
        </TabsContent>
        <TabsContent value="comments" className="pt-4">
          <StudentCommentsTab semester={semester} school_year={school_year} />
        </TabsContent>
        <TabsContent value="reports" className="pt-4">
          <GenerateReportsTab semester={semester} school_year={school_year} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Subject Narratives Tab ============
function SubjectNarrativesTab({ semester, school_year }) {
  const [subjects, setSubjects] = useState(DEFAULT_NARRATIVE_SUBJECTS);
  const [drafts, setDrafts] = useState({}); // {subject: text}
  const [dirty, setDirty] = useState({}); // {subject: bool}
  const [savingSubject, setSavingSubject] = useState(null);
  const [newSubject, setNewSubject] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listSubjectNarratives({ semester, school_year });
        if (cancelled) return;
        const next = {};
        for (const item of res.data.items) next[item.subject] = item.text || "";
        const knownSubjects = [...new Set([...DEFAULT_NARRATIVE_SUBJECTS, ...res.data.items.map((i) => i.subject)])];
        setSubjects(knownSubjects);
        setDrafts(next);
        setDirty({});
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load narratives");
      }
    })();
    return () => { cancelled = true; };
  }, [semester, school_year]);

  const handleSave = async (subject) => {
    setSavingSubject(subject);
    try {
      await upsertSubjectNarrative({ subject, semester, school_year, text: drafts[subject] || "" });
      toast.success(`${subject} narrative saved`);
      setDirty((d) => ({ ...d, [subject]: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingSubject(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subject Narratives — {semester}</CardTitle>
          <CardDescription>
            Write the master language for each subject. This text is the same for all students and appears at the top of every Progress Report PDF. Student-specific additions live in the next tab.
          </CardDescription>
        </CardHeader>
      </Card>
      {subjects.map((subject) => (
        <Card key={subject} data-testid={`narrative-card-${subject.replace(/\s+/g, "-").toLowerCase()}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{subject}</CardTitle>
              <Button
                size="sm" disabled={savingSubject === subject || !dirty[subject]}
                onClick={() => handleSave(subject)}
                className="bg-[#00305E] hover:bg-[#00305E]/90"
                data-testid={`save-narrative-${subject.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {savingSubject === subject ? "Saving…" : dirty[subject] ? "Save" : "Saved"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <textarea
              value={drafts[subject] || ""}
              onChange={(e) => { setDrafts((p) => ({ ...p, [subject]: e.target.value })); setDirty((d) => ({ ...d, [subject]: true })); }}
              className="w-full min-h-[140px] rounded-md border bg-background p-3 text-sm"
              placeholder={`This semester in ${subject}, we focused on…`}
              data-testid={`narrative-textarea-${subject.replace(/\s+/g, "-").toLowerCase()}`}
            />
          </CardContent>
        </Card>
      ))}
      <Card>
        <CardContent className="pt-4 flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Add additional subject</Label>
            <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="e.g., Art, Health, PE" data-testid="new-narrative-subject-input" />
          </div>
          <Button
            onClick={() => {
              if (newSubject.trim() && !subjects.includes(newSubject.trim())) {
                setSubjects((s) => [...s, newSubject.trim()]);
                setDrafts((d) => ({ ...d, [newSubject.trim()]: "" }));
                setDirty((d) => ({ ...d, [newSubject.trim()]: true }));
                setNewSubject("");
              }
            }}
            data-testid="add-narrative-subject-btn"
          >+ Add</Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Student Comments Tab ============
function StudentCommentsTab({ semester, school_year }) {
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState(DEFAULT_NARRATIVE_SUBJECTS);
  const [comments, setComments] = useState({}); // {`${studentId}|${subject}`: text}
  const [dirty, setDirty] = useState({});
  const [selectedSubject, setSelectedSubject] = useState(DEFAULT_NARRATIVE_SUBJECTS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, narr, comm] = await Promise.all([
          getProgressEligibleStudents(semester, school_year),
          listSubjectNarratives({ semester, school_year }),
          listStudentComments({ semester, school_year }),
        ]);
        setStudents(s.data);
        const allSubjects = [...new Set([...DEFAULT_NARRATIVE_SUBJECTS, ...narr.data.items.map((i) => i.subject)])];
        setSubjects(allSubjects);
        const next = {};
        for (const c of comm.data.items) next[`${c.student_id}|${c.subject}`] = c.text || "";
        setComments(next);
        setDirty({});
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load comments");
      }
    })();
  }, [semester, school_year]);

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")),
    [students],
  );

  const handleSaveAll = async () => {
    const entries = Object.keys(dirty).filter((k) => dirty[k]);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      for (const key of entries) {
        const [student_id, subject] = key.split("|");
        await upsertStudentComment({ student_id, subject, semester, school_year, text: comments[key] || "" });
      }
      toast.success(`Saved ${entries.length} comment${entries.length === 1 ? "" : "s"}`);
      setDirty({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.values(dirty).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Subject</Label>
            <select
              value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}
              className="rounded-md border bg-background px-3 py-2 text-sm"
              data-testid="comments-subject-select"
            >
              {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button onClick={handleSaveAll} disabled={saving || dirtyCount === 0} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="comments-save-btn">
            {saving ? "Saving…" : dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "Save"}
          </Button>
        </CardContent>
      </Card>
      <div className="space-y-3">
        {sortedStudents.map((s) => {
          const key = `${s.id}|${selectedSubject}`;
          return (
            <Card key={s.id} data-testid={`comment-card-${s.id}`}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{s.first_name} {s.last_name}</CardTitle></CardHeader>
              <CardContent>
                <textarea
                  value={comments[key] || ""}
                  onChange={(e) => { setComments((p) => ({ ...p, [key]: e.target.value })); setDirty((d) => ({ ...d, [key]: true })); }}
                  className={`w-full min-h-[80px] rounded-md border bg-background p-2 text-sm ${dirty[key] ? "ring-2 ring-amber-400" : ""}`}
                  placeholder={`Specific notes about ${s.preferred_name || s.first_name}'s ${selectedSubject} progress this semester…`}
                  data-testid={`comment-textarea-${s.id}`}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============ Generate Reports Tab ============
function GenerateReportsTab({ semester, school_year }) {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState({ archived_student_ids: [], archived_count: 0, missing_count: 0 });
  // "all" | "summer" (summer-program only) | "regular" (non-summer). Auto-scopes to
  // summer-program students when a Summer semester is selected.
  const [studentFilter, setStudentFilter] = useState("all");

  const loadArchiveStatus = async () => {
    try {
      const res = await getProgressArchiveStatus(semester, school_year);
      setArchiveStatus(res.data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    // Load the Progress Tracking roster for this period: excludes referral/
    // in-progress/waitlist, and includes graduated students only for periods
    // within their enrollment. Summer scoping is applied below via studentFilter.
    getProgressEligibleStudents(semester, school_year).then((res) => setStudents(res.data)).catch(() => {});
    loadArchiveStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semester, school_year]);

  // Default the student scope from the selected semester (summer → summer-only).
  useEffect(() => {
    setStudentFilter(/summer/i.test(semester || "") ? "summer" : "all");
    setSelected(new Set());
  }, [semester]);

  const sorted = useMemo(() => {
    let list = students;
    if (studentFilter === "summer") list = list.filter((s) => s.attending_summer_school);
    else if (studentFilter === "regular") list = list.filter((s) => !s.attending_summer_school);
    return [...list].sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
  }, [students, studentFilter]);
  const archivedSet = useMemo(() => new Set(archiveStatus.archived_student_ids || []), [archiveStatus]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelected(new Set(sorted.map((s) => s.id)));
  const selectMissing = () => setSelected(new Set(sorted.filter((s) => !archivedSet.has(s.id)).map((s) => s.id)));
  const clear = () => setSelected(new Set());

  // Summer is tracking-only: no progress report is generated. Fall/Spring unchanged.
  const summerLocked = isSummerSemester(semester);

  const generateOne = async (studentId) => {
    const url = progressReportPdfUrl(studentId, semester, school_year);
    try {
      setGenerating(true);
      const student = students.find((s) => s.id === studentId);
      await downloadFromApi(url, `Progress_Report_${student?.first_name}_${student?.last_name}_${semester}.pdf`);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const generateBatch = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one student");
      return;
    }
    setGenerating(true);
    try {
      const url = progressReportBatchPdfUrl([...selected], semester, school_year);
      await downloadFromApi(url, `Progress_Reports_${semester}.zip`);
      toast.success(`Downloaded ${selected.size} reports as ZIP`);
    } catch {
      toast.error("Batch generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const archiveToStudentFiles = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one student");
      return;
    }
    setArchiving(true);
    try {
      const res = await saveProgressReportsToFiles({
        student_ids: [...selected], semester, school_year,
      });
      toast.success(`Saved ${res.data.saved} report${res.data.saved === 1 ? "" : "s"} to Student Files`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} failed — see console`);
        console.warn("Archive errors:", res.data.errors);
      }
      await loadArchiveStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base">Generate Progress Reports — {semester}</CardTitle>
              <CardDescription>
                Download PDFs or archive them permanently into each student&apos;s Files (under &quot;Progress Reports&quot;).
                Showing <strong>{sorted.length}</strong> student{sorted.length === 1 ? "" : "s"}
                {studentFilter === "summer" ? " (Summer Program only)" : studentFilter === "regular" ? " (non-summer)" : ""}.
                Already archived: <strong>{archiveStatus.archived_count}</strong> · Missing: <strong>{archiveStatus.missing_count}</strong>.
              </CardDescription>
            </div>
            {!summerLocked && (
            <div className="flex gap-2 flex-wrap items-center">
              <Select value={studentFilter} onValueChange={(v) => { setStudentFilter(v); setSelected(new Set()); }}>
                <SelectTrigger className="w-48" data-testid="reports-student-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All current students</SelectItem>
                  <SelectItem value="summer">Summer Program only</SelectItem>
                  <SelectItem value="regular">Non-summer students</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={selectMissing} data-testid="reports-select-missing">Select missing ({archiveStatus.missing_count})</Button>
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="reports-select-all">Select all</Button>
              <Button variant="outline" size="sm" onClick={clear} data-testid="reports-clear">Clear</Button>
              <Button onClick={generateBatch} disabled={generating || selected.size === 0} variant="outline" data-testid="reports-batch-btn">
                {generating ? "…" : `Download ${selected.size || ""} PDF${selected.size === 1 ? "" : "s"}`}
              </Button>
              <Button onClick={archiveToStudentFiles} disabled={archiving || selected.size === 0} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="reports-archive-btn">
                {archiving ? "Archiving…" : `Archive ${selected.size || ""} to Student Files`}
              </Button>
            </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {summerLocked ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="reports-summer-note">
              Summer is <strong>tracking-only</strong> — no progress report is generated for the summer term.
              Summer scores, narratives, and comments are captured for internal visibility on the other tabs.
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sorted.map((s) => {
              const isArchived = archivedSet.has(s.id);
              return (
                <label key={s.id} className={`flex items-center gap-2 border rounded-md px-3 py-2 hover:bg-muted/30 cursor-pointer ${isArchived ? "bg-green-50/50 border-green-200" : ""}`} data-testid={`reports-student-${s.id}`}>
                  <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} />
                  <div className="flex-1 text-sm">
                    {s.first_name} {s.last_name}
                    {isArchived && <span className="ml-2 text-[10px] text-green-700 font-semibold">✓ Archived</span>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.preventDefault(); generateOne(s.id); }} data-testid={`reports-one-${s.id}`}>PDF</Button>
                </label>
              );
            })}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
